import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Avatar,
  Chip,
  Button,
  Container,
  Breadcrumbs,
  Link,
  Divider,
  IconButton,
  Skeleton,
  useMediaQuery,
  Paper,
  Stack,
  Checkbox,
  Fab,
  Badge,
} from "@mui/material";
import { BadgeProps } from "@mui/material/Badge";
import { styled } from "@mui/material/styles";
import { useTheme } from "@mui/material/styles";
import { useParams, useHistory, useLocation } from "react-router-dom";
import { getUserData } from "../../../common/utils";
import PersonIcon from "@mui/icons-material/Person";
import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import WorkIcon from "@mui/icons-material/Work";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import PendingIcon from "@mui/icons-material/Pending";
import InterviewIcon from "@mui/icons-material/RecordVoiceOver";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import MailOutlineIcon from "@mui/icons-material/MailOutline";
import MailIcon from "@mui/icons-material/Mail";
import SelectAllIcon from "@mui/icons-material/SelectAll";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CandidateProfile from "./CandidateProfile";
import EmailComposer from "./EmailComposer";
import styles from "./BusinessOwnerProjectDetails.module.css";
import { rateStaff } from "../../../Services/BusinessOwner";
import { Margin } from "@mui/icons-material";
import zIndex from "@mui/material/styles/zIndex";
import { IonContent, IonPage } from "@ionic/react";

type Candidate = {
  user_id: number;
  full_name: string;
  email_address: string;
  phone_number: string;
  location_city: string;
  location_country: string;
  total_years_experience: string;
  status: {
    employed: number;
    interviewed: number;
    screened: number;
  };
};

type JobRole = {
  jobRoleID: string;
  jobTitle: string;
  candidates: Candidate[];
};

type Project = {
  project_id: string;
  project_name: string;
  jobRoles: JobRole[];
  status: string;
  tags: string;
  created_at: string;
};

const StyledBadge = styled(Badge)<BadgeProps>(({ theme }) => ({
  "& .MuiBadge-badge": {
    right: 18,
    top: 24,
    padding: "0 6px", // more horizontal padding for multi-digit
    fontSize: "0.55rem",
    minWidth: 22, // ensure enough width for 2–3 digits
    height: 22, // consistent height
    borderRadius: "12px", // makes it pill-shaped for longer numbers
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: theme.zIndex.tooltip,
  },
  "&.MuiBadge-dot": {
    borderRadius: "50%",
    width: 8,
    height: 8,
  },
}));

const ProjectDetail = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const location = useLocation();
  const history = useHistory();
  const user = getUserData();
  console.log("Location state:", location.state);
  const queryParams = new URLSearchParams(location.search);
  const projectId = queryParams.get("id");
  // const projectData = (location.state as Project) ?? null
  const projectData =
    (location.state as { project: Project } | null)?.project ?? null;

  console.log("projectData:", projectData);
  console.log("Query Params ID:", projectId);
  // const { id } = useParams()

  const [project, setProject] = useState<Project | null>(projectData);
  const [loading, setLoading] = useState(true);
  const [selectedRole, setSelectedRole] = useState<string>("all");
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCandidates, setSelectedCandidates] = useState<Set<string>>(
    new Set()
  );
  const [emailComposerOpen, setEmailComposerOpen] = useState(false);
  const [candidateProfileOpen, setCandidateProfileOpen] = useState(false);
  const [selectedCandidate, setSelectedCandidate] = useState<any>(null);
  // useEffect(() => {
  //   // Simulate fetch
  //   setTimeout(() => {
  //     setProject(projectData)
  //       setLoading(false)
  //   }, 500)
  // }, [projectId])

  useEffect(() => {
    if (projectData && projectId) {
      // Fetch project data if not passed via state
      // This would be your API call
      setProject(projectData);
      setLoading(false);
    }
  }, [projectId, projectData]);

  const getStatusInfo = (status: Candidate["status"]) => {
    if (Number(status.employed) === 1) {
      return {
        label: "Hired",
        color: "success" as const,
        icon: <CheckCircleIcon />,
      };
    } else if (Number(status.interviewed) === 1) {
      return {
        label: "Interviewed",
        color: "warning" as const,
        icon: <InterviewIcon />,
      };
    } else if (Number(status.screened) === 1) {
      return {
        label: "Screened",
        color: "info" as const,
        icon: <PendingIcon />,
      };
    }
    return {
      label: "Applied",
      color: "default" as const,
      icon: <PersonIcon />,
    };
  };

  const getAllCandidates = () => {
    if (!project) return [];
    console.log("Project Data:", project);
    return project?.jobRoles?.flatMap((role) =>
      role?.candidates?.map((candidate) => ({
        ...candidate,
        jobTitle: role.jobTitle,
        jobRoleID: role.jobRoleID,
      }))
    );
  };

  const getFilteredCandidates = () => {
    let candidates = getAllCandidates();

    if (selectedRole !== "all") {
      candidates = candidates.filter((c) => c.jobRoleID === selectedRole);
    }

    if (searchTerm) {
      candidates = candidates.filter(
        (c) =>
          c.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.email_address.toLowerCase().includes(searchTerm.toLowerCase()) ||
          c.jobTitle.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return candidates;
  };

  const getJobRoleStats = (roleId: string) => {
    const role = project?.jobRoles.find((r) => r.jobRoleID === roleId);
    if (!role) return { total: 0, hired: 0, interviewed: 0, screened: 0 };

    return {
      total: role.candidates.length,
      hired: role.candidates.filter((c) => c.status.employed === 1).length,
      interviewed: role.candidates.filter((c) => c.status.interviewed === 1)
        .length,
      screened: role.candidates.filter((c) => c.status.screened === 1).length,
    };
  };

  const handleCandidateSelect = (candidateId: string) => {
    const newSelected = new Set(selectedCandidates);
    if (newSelected.has(candidateId)) {
      newSelected.delete(candidateId);
    } else {
      newSelected.add(candidateId);
    }
    setSelectedCandidates(newSelected);
  };

  const handleSelectAll = () => {
    const filteredCandidates = getFilteredCandidates();
    const allIds = new Set(filteredCandidates.map((c) => c.user_id.toString()));
    setSelectedCandidates(allIds);
  };

  const handleClearSelection = () => {
    setSelectedCandidates(new Set());
  };

  const handleBulkEmail = () => {
    const filteredCandidates = getFilteredCandidates();
    const selectedCandidateData = filteredCandidates.filter((c) =>
      selectedCandidates.has(c.user_id.toString())
    );
    setEmailComposerOpen(true);
  };

  const handleViewProfile = (candidate: any) => {
    setSelectedCandidate(candidate);
    setCandidateProfileOpen(true);
  };

  const handleSendEmail = async (emailData: any) => {
    console.log("Sending email:", emailData);
    return Promise.resolve();
  };

  const handleUpdateStatus = (candidateId: string, status: string) => {
    console.log("Updating status:", candidateId, status);
  };

  const handleRateStaff = async (ratingData: any) => {
    try {
      console.log("Rating staff:", ratingData);
      await rateStaff(ratingData);
      // You could add a success notification here
      console.log("Staff rating submitted successfully");
    } catch (error) {
      console.error("Failed to submit staff rating:", error);
      throw error;
    }
  };

  const getSelectedCandidatesData = () => {
    const filteredCandidates = getFilteredCandidates();
    return filteredCandidates.filter((c) =>
      selectedCandidates.has(c.user_id.toString())
    );
  };

  if (loading) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Box className={styles.loadingContainer}>
          {[...Array(6)].map((_, i) => (
            <Card key={i} className={styles.candidateCard}>
              <CardContent>
                <Box display="flex" gap={2}>
                  <Skeleton variant="circular" width={80} height={80} />
                  <Box flex={1}>
                    <Skeleton height={24} width="60%" />
                    <Skeleton height={20} width="40%" sx={{ mt: 1 }} />
                    <Skeleton height={20} width="80%" sx={{ mt: 1 }} />
                  </Box>
                </Box>
              </CardContent>
            </Card>
          ))}
        </Box>
      </Container>
    );
  }

  if (!project) {
    return (
      <Container maxWidth="xl" className={styles.container}>
        <Typography variant="h6" color="error">
          Project not found
        </Typography>
      </Container>
    );
  }
  console.log("Project:", project);
  console.log("projectname:", project.project_name);
  const filteredCandidates = getFilteredCandidates();
  const totalCandidates = getAllCandidates()?.length;

  return (
    <IonPage>
      <IonContent>
        <div className={styles.divContainer}>
          <Container maxWidth="xl" className={styles.container}>
            {/* Header Section */}
            <Box className={styles.header}>
              <Box className={styles.headerTop}>
                <IconButton
                  onClick={() => history.goBack()}
                  className={styles.backButton}
                >
                  <ArrowBackIcon />
                </IconButton>
                <Box>
                  <Typography variant="h4" className={styles.pageTitle}>
                    {project.project_name} - Applicants
                  </Typography>
                  <Breadcrumbs className={styles.breadcrumbs}>
                    <Link
                      color="inherit"
                      onClick={() =>
                        history.push("/folder/businessOwnerDashboard")
                      }
                    >
                      Dashboard
                    </Link>
                    {/* <Typography color="text.primary">Applicants</Typography> */}
                  </Breadcrumbs>
                </Box>
              </Box>

              {/* Stats Overview */}
              <Grid container spacing={2} className={styles.statsGrid}>
                <Grid item xs={6} sm={3}>
                  <Paper className={styles.statCard}>
                    <Typography variant="h3" className={styles.statNumber}>
                      {totalCandidates}
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Total Applicants
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper className={styles.statCard}>
                    <Typography variant="h3" className={styles.statNumber}>
                      {
                        getAllCandidates()?.filter(
                          (c) => Number(c.status.screened) === 1
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Screened
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper className={styles.statCard}>
                    <Typography variant="h3" className={styles.statNumber}>
                      {
                        getAllCandidates()?.filter(
                          (c) => Number(c.status.interviewed) === 1
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Interviewed
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={6} sm={3}>
                  <Paper className={styles.statCard}>
                    <Typography variant="h3" className={styles.statNumber}>
                      {
                        getAllCandidates()?.filter(
                          (c) => Number(c.status.employed) === 1
                        ).length
                      }
                    </Typography>
                    <Typography variant="body2" className={styles.statLabel}>
                      Hired
                    </Typography>
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Filters Section */}
            <Paper className={styles.filtersSection}>
              <Box className={styles.filtersContent}>
                <Box className={styles.roleFilters}>
                  <Button
                    variant={selectedRole === "all" ? "contained" : "outlined"}
                    onClick={() => setSelectedRole("all")}
                    className={styles.filterButton}
                  >
                    All Roles ({totalCandidates})
                  </Button>
                  {project?.jobRoles?.map((role) => {
                    const stats = getJobRoleStats(role.jobRoleID);
                    return (
                      <Button
                        key={role.jobRoleID}
                        variant={
                          selectedRole === role.jobRoleID
                            ? "contained"
                            : "outlined"
                        }
                        onClick={() => setSelectedRole(role.jobRoleID)}
                        className={styles.filterButton}
                      >
                        {role.jobTitle || "Untitled Role"} ({stats.total})
                      </Button>
                    );
                  })}
                </Box>
              </Box>
            </Paper>

            {/* Bulk Actions Section */}
            {selectedCandidates.size > 0 && (
              <Paper className={styles.bulkActionsBar}>
                <Box className={styles.bulkActionsContent}>
                  <Typography variant="body1">
                    {selectedCandidates.size} candidate
                    {selectedCandidates.size !== 1 ? "s" : ""} selected
                  </Typography>
                  <Stack direction="row" spacing={2}>
                    <Button
                      variant="outlined"
                      onClick={handleClearSelection}
                      startIcon={<ClearAllIcon />}
                      className={styles.clearSelectionButton}
                    >
                      Clear Selection
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleBulkEmail}
                      startIcon={<EmailIcon />}
                    >
                      Send Email
                    </Button>
                  </Stack>
                </Box>
              </Paper>
            )}

            {/* Candidates Grid */}
            <Box className={styles.candidatesSection}>
              <Box className={styles.sectionHeader}>
                <Typography variant="h6" className={styles.sectionTitle}>
                  {filteredCandidates?.length} Candidate
                  {filteredCandidates?.length !== 1 ? "s" : ""} Found
                </Typography>
                <Stack direction="row" spacing={2}>
                  <Button
                    variant="outlined"
                    onClick={handleSelectAll}
                    startIcon={<SelectAllIcon />}
                    size="small"
                  >
                    Select All
                  </Button>
                </Stack>
              </Box>

              <Grid container spacing={3}>
                {filteredCandidates?.map((candidate, index) => {
                  const statusInfo = getStatusInfo(candidate.status);
                  const isSelected = selectedCandidates.has(
                    candidate.user_id.toString()
                  );

                  return (
                    <Grid
                      item
                      xs={12}
                      md={6}
                      lg={4}
                      key={`${candidate.user_id}-${index}`}
                    >
                      <Card
                        className={`${styles.candidateCard} ${
                          isSelected ? styles.selectedCard : ""
                        }`}
                      >
                        <CardContent className={styles.candidateContent}>
                          <Box className={styles.cardHeader}>
                            <Checkbox
                              checked={isSelected}
                              onChange={() =>
                                handleCandidateSelect(
                                  candidate.user_id.toString()
                                )
                              }
                              className={styles.selectionCheckbox}
                            />
                          </Box>

                          <Box className={styles.candidateHeader}>
                            <Avatar
                              className={styles.candidateAvatar}
                              src={`/placeholder.svg?height=80&width=80&query=professional headshot of ${candidate.full_name}`}
                            >
                              <PersonIcon />
                            </Avatar>
                            <Box className={styles.candidateInfo}>
                              <Typography
                                variant="h6"
                                className={styles.candidateName}
                              >
                                {candidate.full_name}
                              </Typography>
                              <Typography
                                variant="body2"
                                className={styles.candidateRole}
                              >
                                {candidate.jobTitle || "General Application"}
                              </Typography>
                              <Chip
                                {...statusInfo}
                                size="small"
                                className={styles.statusChip}
                              />
                            </Box>
                          </Box>

                          <Divider className={styles.divider} />

                          <Stack
                            spacing={2}
                            className={styles.candidateDetails}
                          >
                            <Box className={styles.detailItem}>
                              <EmailIcon className={styles.detailIcon} />
                              <Typography
                                variant="body2"
                                className={styles.detailText}
                              >
                                {candidate.email_address}
                              </Typography>
                            </Box>

                            <Box className={styles.detailItem}>
                              <PhoneIcon className={styles.detailIcon} />
                              <Typography
                                variant="body2"
                                className={styles.detailText}
                              >
                                {candidate.phone_number}
                              </Typography>
                            </Box>

                            <Box className={styles.detailItem}>
                              <LocationOnIcon className={styles.detailIcon} />
                              <Typography
                                variant="body2"
                                className={styles.detailText}
                              >
                                {candidate.location_city},{" "}
                                {candidate.location_country}
                              </Typography>
                            </Box>

                            <Box className={styles.detailItem}>
                              <WorkIcon className={styles.detailIcon} />
                              <Typography
                                variant="body2"
                                className={styles.detailText}
                              >
                                {Number(
                                  candidate.total_years_experience
                                ).toFixed(0)}{" "}
                                {Number(candidate.total_years_experience) === 1
                                  ? "year"
                                  : "years"}{" "}
                                experience
                              </Typography>
                            </Box>
                          </Stack>

                          <Box className={styles.candidateActions}>
                            <Button
                              variant="contained"
                              size="small"
                              className={styles.primaryAction}
                              onClick={() => handleViewProfile(candidate)}
                            >
                              View Profile
                            </Button>
                            <Button
                              variant="outlined"
                              size="small"
                              className={styles.secondaryAction}
                              onClick={() => {
                                setSelectedCandidate(candidate);
                                setSelectedCandidates(
                                  new Set([candidate.user_id.toString()])
                                );
                                setEmailComposerOpen(true);
                              }}
                              startIcon={<EmailIcon />}
                            >
                              Email
                            </Button>
                          </Box>
                        </CardContent>
                      </Card>
                    </Grid>
                  );
                })}
              </Grid>

              {/* Empty State */}
              {filteredCandidates?.length === 0 && (
                <Paper className={styles.emptyState}>
                  <PersonIcon className={styles.emptyIcon} />
                  <Typography variant="h6" className={styles.emptyTitle}>
                    No candidates found
                  </Typography>
                  <Typography
                    variant="body2"
                    className={styles.emptyDescription}
                  >
                    Try adjusting your filters or check back later for new
                    applications.
                  </Typography>
                </Paper>
              )}
            </Box>

            {/* Modals for Candidate Profile and Email Composer */}
            <CandidateProfile
              open={candidateProfileOpen}
              onClose={() => setCandidateProfileOpen(false)}
              candidate={selectedCandidate}
              onSendEmail={(candidate) => {
                setSelectedCandidates(new Set([candidate.user_id.toString()]));
                setCandidateProfileOpen(false);
                setEmailComposerOpen(true);
              }}
              onUpdateStatus={handleUpdateStatus}
              project={project}
              onRateStaff={handleRateStaff}
            />

            <EmailComposer
              open={emailComposerOpen}
              onClose={() => setEmailComposerOpen(false)}
              recipients={getSelectedCandidatesData()}
              project={project}
              selectedRole={selectedRole}
              // onSendEmail={handleSendEmail}
            />
          </Container>

          {/* Floating Action Button for Bulk Email */}
          {selectedCandidates.size > 0 && (
            <Fab
              color="primary"
              className={styles.emailFab}
              // sx={{ position: "fixed", bottom: 216, right: 16, zIndex: 3000,  }}
              onClick={handleBulkEmail}
            >
              <StyledBadge
                badgeContent={selectedCandidates.size}
                color="secondary"
                // overlap="circular"
                style={{ margin: "32px" }}
              >
                {/* <MailOutlineIcon /> */}
                <MailIcon fontSize="large" />
              </StyledBadge>
            </Fab>
          )}
        </div>
      </IonContent>
    </IonPage>
  );
};

export default ProjectDetail;
